import type { SessionUser } from '@lawyer/shared'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { UNAUTHORIZED_EVENT, api } from '../lib/api'
import { queryClient } from '../lib/queryClient'

export type AuthConfig = { googleEnabled: boolean; devLoginEnabled: boolean }

type AuthContextValue = {
  user: SessionUser | null
  config: AuthConfig
  loading: boolean
  signInWithDevLogin: (email: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [config, setConfig] = useState<AuthConfig>({ googleEnabled: false, devLoginEnabled: false })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    const bootstrap = async () => {
      const [configResult, meResult] = await Promise.allSettled([
        api.get<AuthConfig>('/auth/config'),
        api.get<{ user: SessionUser }>('/auth/me'),
      ])
      if (cancelled) return

      if (configResult.status === 'fulfilled') setConfig(configResult.value)
      setUser(meResult.status === 'fulfilled' ? meResult.value.user : null)
      setLoading(false)
    }

    void bootstrap()
    return () => {
      cancelled = true
    }
  }, [])

  // A 401 from any request means the session is gone — drop it everywhere at once.
  useEffect(() => {
    const onUnauthorized = () => {
      setUser(null)
      queryClient.clear()
    }
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized)
  }, [])

  const signInWithDevLogin = useCallback(async (email: string) => {
    const result = await api.post<{ user: SessionUser }>('/auth/dev-login', { email })
    setUser(result.user)
  }, [])

  const logout = useCallback(async () => {
    await api.post('/auth/logout')
    setUser(null)
    queryClient.clear()
  }, [])

  const value = useMemo(
    () => ({ user, config, loading, signInWithDevLogin, logout }),
    [user, config, loading, signInWithDevLogin, logout]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
